/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable eqeqeq */
/* eslint-disable max-lines */
'use client';

import * as React from 'react';

import {
	Button,
	Pagination as ChakraPagination,
	IconButton,
	Text,
	createContext,
	usePaginationContext,
} from '@chakra-ui/react';
import type { ButtonProps, TextProps } from '@chakra-ui/react';
import {
	HiChevronLeft,
	HiChevronRight,
	HiMiniEllipsisHorizontal,
} from 'react-icons/hi2';

import { LinkButton } from './link-button';

type ButtonVariantMap = {
	current:  ButtonProps['variant'];
	default:  ButtonProps['variant'];
	ellipsis: ButtonProps['variant'];
};

type PaginationVariant = 'outline' | 'solid' | 'subtle';

type ButtonVariantContext = {
	size:       ButtonProps['size'];
	variantMap: ButtonVariantMap;
	getHref?:   (page: number) => string;
};

const [ RootPropsProvider, useRootProps ] = createContext<ButtonVariantContext>({ name: 'RootPropsProvider' });

export type PaginationRootProps = {
	size?:    ButtonProps['size'];
	variant?: PaginationVariant;
	getHref?: (page: number) => string;
} & Omit<ChakraPagination.RootProps, 'type'>;

const variantMap: Record<PaginationVariant, ButtonVariantMap> = {
	outline: {
		default:  'ghost',
		ellipsis: 'plain',
		current:  'outline',
	},
	solid: {
		default:  'outline',
		ellipsis: 'outline',
		current:  'solid',
	},
	subtle: {
		default:  'ghost',
		ellipsis: 'plain',
		current:  'subtle',
	},
};

export const PaginationRoot = React.forwardRef<
	HTMLDivElement,
	PaginationRootProps
>((props, ref) => {
	const {
		size = 'sm', variant = 'outline', getHref, ...rest
	} = props;

	return (
		<RootPropsProvider
			value={{
				size,
				variantMap: variantMap[variant],
				getHref,
			}}
		>
			<ChakraPagination.Root
				ref={ref}
				type={getHref ? 'link' : 'button'}
				{...rest}
			/>
		</RootPropsProvider>
	);
});

PaginationRoot.displayName = 'PaginationRoot';

export const PaginationEllipsis = React.forwardRef<
	HTMLDivElement,
	ChakraPagination.EllipsisProps
>((props, ref) => {
	const { size, variantMap } = useRootProps();

	return (
		<ChakraPagination.Ellipsis ref={ref} {...props} asChild>
			<Button as={'span'} size={size} variant={variantMap.ellipsis}>
				<HiMiniEllipsisHorizontal />
			</Button>
		</ChakraPagination.Ellipsis>
	);
});

PaginationEllipsis.displayName = 'PaginationEllipsis';

export const PaginationItem = React.forwardRef<
	HTMLButtonElement,
	ChakraPagination.ItemProps
>((props, ref) => {
	const { page } = usePaginationContext();
	const { size, variantMap, getHref } = useRootProps();

	const current = page === props.value;
	const variant = current ? variantMap.current : variantMap.default;

	if (getHref) {
		return (
			<LinkButton href={getHref(props.value)} size={size} variant={variant}>
				{props.value}
			</LinkButton>
		);
	}

	return (
		<ChakraPagination.Item ref={ref} {...props} asChild>
			<Button size={size} variant={variant}>
				{props.value}
			</Button>
		</ChakraPagination.Item>
	);
});

PaginationItem.displayName = 'PaginationItem';

export const PaginationPrevTrigger = React.forwardRef<
	HTMLButtonElement,
	ChakraPagination.PrevTriggerProps
>((props, ref) => {
	const { size, variantMap, getHref } = useRootProps();
	const { previousPage } = usePaginationContext();

	if (getHref) {
		return (
			<LinkButton
				href={previousPage != null ? getHref(previousPage) : undefined}
				size={size}
				variant={variantMap.default}
			>
				<HiChevronLeft />
			</LinkButton>
		);
	}

	return (
		<ChakraPagination.PrevTrigger ref={ref} asChild {...props}>
			<IconButton size={size} variant={variantMap.default}>
				<HiChevronLeft />
			</IconButton>
		</ChakraPagination.PrevTrigger>
	);
});

PaginationPrevTrigger.displayName = 'PaginationPrevTrigger';

export const PaginationNextTrigger = React.forwardRef<
	HTMLButtonElement,
	ChakraPagination.NextTriggerProps
>((props, ref) => {
	const { size, variantMap, getHref } = useRootProps();
	const { nextPage } = usePaginationContext();

	if (getHref) {
		return (
			<LinkButton
				href={nextPage != null ? getHref(nextPage) : undefined}
				size={size}
				variant={variantMap.default}
			>
				<HiChevronRight />
			</LinkButton>
		);
	}

	return (
		<ChakraPagination.NextTrigger ref={ref} asChild {...props}>
			<IconButton size={size} variant={variantMap.default}>
				<HiChevronRight />
			</IconButton>
		</ChakraPagination.NextTrigger>
	);
});

PaginationNextTrigger.displayName = 'PaginationNextTrigger';

export const PaginationItems = (props: React.HTMLAttributes<HTMLElement>) => {
	return (
		<ChakraPagination.Context>
			{({ pages }) => pages.map((page, index) => {
				return page.type === 'ellipsis'
					? <PaginationEllipsis key={index} index={index} {...props} />
					: (
						<PaginationItem
							key={index}
							type={'page'}
							value={page.value}
							{...props}
						/>
					);
			})}
		</ChakraPagination.Context>
	);
};

type PageTextProps = {
	format?: 'short' | 'compact' | 'long';
} & TextProps;

export const PaginationPageText = React.forwardRef<
	HTMLParagraphElement,
	PageTextProps
>((props, ref) => {
	const { format = 'compact', ...rest } = props;
	const {
		page, totalPages, pageRange, count,
	} = usePaginationContext();
	const content = React.useMemo(() => {
		if (format === 'short') return `${page} / ${totalPages}`;

		if (format === 'compact') return `${page} of ${totalPages}`;

		return `${pageRange.start + 1} - ${Math.min(pageRange.end, count)} of ${count}`;
	}, [ format, page, totalPages, pageRange, count ]);

	return (
		<Text ref={ref} fontWeight={'medium'} {...rest}>
			{content}
		</Text>
	);
});

PaginationPageText.displayName = 'PaginationPageText';
